import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { formatAgoIfIn12TimestampOrString, formatAgoIfIn24TimestampOrString } from 'util/formatAgo';
import { IWeatherItem } from 'api/resort/types';
import { NameDay } from 'components/resort/ResortWeatherRow/NameDay';
import { DistanceUnit, SnowUnit, TemperatureUnit } from 'components/blocks/Unit';
import { IconWeather } from 'components/resort/IconWeather';
import { WeatherType } from 'types';
import classNames from 'classnames';

import { ReactComponent as SvgWind } from 'public/assets/icons/wind.svg';
import { ReactComponent as SvgClouds } from 'public/assets/icons/clouds.svg';
import styles from './styles.module.scss';

export interface IResortWeatherRow {
  title: string,
  updatedAt: string,
  weatherDescription: string,
  base: string,
  summit: string,
  wind: number,
  snow: number,
  uuid: number,
}

export interface IPropsResortWeatherRow {
  resort: IWeatherItem;
  className: string;
  weatherType: WeatherType;
  isHourly?: boolean;
}

export const ResortWeatherRowHeader = (
  { updatedAt }: { updatedAt?: string, summit: number, base: number },
) => {
  const { t } = useOTSTranslation('common');

  return (
    <thead className={styles.box}>
      <th className={styles.title}>
        {
        updatedAt ? (
          <>
            <span className={classNames(styles.date, styles.title)}><NameDay date={updatedAt} /></span>
            <time title={t('resort.weather.toolTipUpdated')}>
              {formatAgoIfIn24TimestampOrString(updatedAt as string)}
            </time>
          </>
        ) : null
      }
      </th>
      <th className={styles.base}>
        <span className={styles.h5}>
          {t('common.base')}
        </span>
      </th>
      <th className={styles.summit}>
        <span className={styles.h5}>
          {t('common.summit')}
        </span>
      </th>
      <th className={classNames(styles.icon, styles.wind)} title={t('common.windSpeed')}>
        <SvgWind />
      </th>
      <th className={classNames(styles.icon, styles.snow)} title={t('common.forecastedSnow')}>
        <SvgClouds />
      </th>
    </thead>
  );
};

export const ResortWeatherRow = (
  {
    resort, className, isHourly, weatherType,
  }: IPropsResortWeatherRow,
) => {
  const { t } = useOTSTranslation('common');

  const TitleRow = () => (
    isHourly ? (
      <span className={classNames(styles.h4, styles.title)}>
        {formatAgoIfIn12TimestampOrString(resort.date)}
      </span>
    ) : (
      <span className={classNames(styles.h4, styles.title)}><NameDay date={resort.date} /></span>
    )
  );

  return (
    <tr className={classNames(styles.box, className)}>
      <th scope="row" className={styles.title}>
        <div className={styles.titleWrapper}>
          <TitleRow />
          <div className={styles.weatherDescription}>
            <IconWeather title={weatherType} />
          </div>
        </div>
      </th>
      <td className={styles.base}>
        <span className={styles.h4}>
          <TemperatureUnit
            values={[resort.base.min, resort.base.max]}
            type="temperature"
            separator="/"
            removeEqual
          />
        </span>
      </td>
      <td className={styles.summit}>
        <span className={styles.h4}>
          <TemperatureUnit
            values={[resort.summit.min, resort.summit.max]}
            type="temperature"
            separator="/"
            removeEqual
          />
        </span>
      </td>
      <td className={styles.wind} aria-label={t('common.windSpeed')}>
        <span className={styles.h4}><DistanceUnit values={resort.wind} type="kmhOrMileh" /></span>
      </td>
      <td className={styles.snow} aria-label={t('common.forecastedSnow')}>
        <span className={styles.h4}><SnowUnit values={resort.snow} /></span>
      </td>
    </tr>
  );
};
